import { Application, Assets } from "pixi.js";
import type { Texture } from "pixi.js";
import type { PartyMarker, WorldSnapshot } from "@frontier/protocol/contracts";
import type {
  MissionObjective,
  MissionView,
} from "@frontier/content/contracts";
import { Autopilot } from "./autopilot";
import type { Point } from "./geometry";
import { GameInput } from "./input";
import type { GameCallbacks } from "./input";
import { SectorScene } from "./scene";
import { MAP_ASTEROID_ASSETS } from "../map-content";
import { loadCombatTextures } from "./combat-effects";
import { HULL_ART } from "../hull-art";
import { ALIEN_NPC_TEXTURES } from "./npc-appearance";

export type { GameCallbacks, InputIntent } from "./input";

export interface GameController {
  setBackdropTilt: (point: Point) => void;
  setFriends: (ids: readonly string[]) => void;
  setSuspended: (suspended: boolean) => void;
  setSnapshot: (snapshot: WorldSnapshot, receivedAt: number) => void;
  setEventObjective: (objective: MissionObjective | null) => void;
  setFlightIdentity: (runId: string, intro: boolean) => void;
  setTutorialGuidanceVisible: (visible: boolean) => void;
  setMission: (mission: MissionView | null) => void;
  setPartyMarker: (marker: PartyMarker | null) => void;
  setLocalPlayerId: (id: string) => void;
  setControls: (controls: { boost: boolean }) => void;
  selectTarget: (id: string) => void;
  navigate: (point: Point) => void;
  cancelNavigation: () => void;
  dispose: () => void;
}

export async function mountGame(
  element: HTMLElement,
  callbacks: GameCallbacks,
): Promise<GameController> {
  const app = new Application();
  await app.init({
    preference: "webgl",
    width: Math.max(1, element.clientWidth),
    height: Math.max(1, element.clientHeight),
    resolution: Math.min(window.devicePixelRatio || 1, 2),
    autoDensity: true,
    antialias: true,
    background: 0x040b15,
    powerPreference: "high-performance",
  });
  app.canvas.style.display = "block";
  app.canvas.style.width = "100%";
  app.canvas.style.height = "100%";
  app.canvas.style.touchAction = "none";
  app.canvas.style.userSelect = "none";
  app.canvas.setAttribute(
    "aria-label",
    "Игровой сектор Фронтира. Нажмите на пустое место для полёта к точке. Удерживайте левую кнопку мыши для полёта за курсором; после отпускания корабль долетит до последней точки. Нажмите на противника для выбора цели. WASD — дополнительное движение, пробел — форсаж, Q — модуль, E — взаимодействие. На телефоне двигайтесь левым джойстиком и касайтесь целей.",
  );
  element.appendChild(app.canvas);
  let scene: SectorScene;
  try {
    const [
      player,
      playerHeavy,
      engineer,
      scout,
      playerTransport,
      enemy,
      enemyHeavy,
      transport,
      station,
      container,
      asteroidTextures,
      raider,
      sniper,
      minelayer,
      elite,
      support,
      boss,
      alienScout,
      alienArtillery,
      alienDisruptor,
      combat,
      bastion,
      hawk,
      aerie,
      arrow,
      argosy,
      leviathan,
      quicksilver,
      falcon,
      clipper,
      behemoth,
    ] = await Promise.all([
      Assets.load<Texture>(HULL_ART.interceptor.src),
      Assets.load<Texture>(HULL_ART.assault.src),
      Assets.load<Texture>(HULL_ART.engineer.src),
      Assets.load<Texture>(HULL_ART.scout.src),
      Assets.load<Texture>(HULL_ART.transport.src),
      Assets.load<Texture>("/assets/enemy.png"),
      Assets.load<Texture>("/assets/enemy-heavy.png"),
      Assets.load<Texture>("/assets/transport.png"),
      Assets.load<Texture>("/assets/station.png"),
      Assets.load<Texture>("/assets/container.png"),
      Promise.all(
        MAP_ASTEROID_ASSETS.map((path) => Assets.load<Texture>(path)),
      ),
      Assets.load<Texture>("/assets/combat/fighter.png"),
      Assets.load<Texture>("/assets/combat/missile-boat.png"),
      Assets.load<Texture>("/assets/combat/minelayer.png"),
      Assets.load<Texture>("/assets/player-heavy.png"),
      Assets.load<Texture>("/assets/combat/heavy.png"),
      Assets.load<Texture>("/assets/combat/boss.png"),
      Assets.load<Texture>(ALIEN_NPC_TEXTURES.alienScout),
      Assets.load<Texture>(ALIEN_NPC_TEXTURES.alienArtillery),
      Assets.load<Texture>(ALIEN_NPC_TEXTURES.alienDisruptor),
      loadCombatTextures(),
      Assets.load<Texture>(HULL_ART.bastion.src),
      Assets.load<Texture>(HULL_ART.hawk.src),
      Assets.load<Texture>(HULL_ART.aerie.src),
      Assets.load<Texture>(HULL_ART.arrow.src),
      Assets.load<Texture>(HULL_ART.argosy.src),
      Assets.load<Texture>(HULL_ART.leviathan.src),
      Assets.load<Texture>(HULL_ART.quicksilver.src),
      Assets.load<Texture>(HULL_ART.falcon.src),
      Assets.load<Texture>(HULL_ART.clipper.src),
      Assets.load<Texture>(HULL_ART.behemoth.src),

    ]);
    const [asteroidRockA, asteroidRockB, asteroidIron] = asteroidTextures;
    if (!asteroidRockA || !asteroidRockB || !asteroidIron)
      throw new Error("Asteroid texture set is incomplete");
    scene = new SectorScene(app, {
      hulls: {
        assault: playerHeavy,
        interceptor: player,
        engineer,
        scout,
        transport: playerTransport,
        bastion,
        hawk,
        aerie,
        arrow,
        argosy,
        leviathan,
        quicksilver,
        falcon,
        clipper,
        behemoth,
      },
      enemy,
      enemyHeavy,
      transport,
      station,
      asteroids: [asteroidRockA, asteroidRockB, asteroidIron],
      container,
      raider,
      sniper,
      minelayer,
      elite,
      support,
      boss,
      alienScout,
      alienArtillery,
      alienDisruptor,
      combat,
    });
  } catch (error) {
    app.destroy({ removeView: true }, { children: true });
    throw error;
  }
  const navigation = new Autopilot(
    () => scene.getNavigationContext(),
    (state) => callbacks.onNavigation?.(state),
  );
  const input = new GameInput(
    app.canvas,
    {
      ...callbacks,
      onTarget: (id) => {
        scene.selectTarget(id);
        callbacks.onTarget?.(id);
      },
    },
    (point) => scene.toWorld(point),
    (point) => scene.pickTarget(point),
    () => scene.getLocalPosition(),
    () => {
      const context = scene.getNavigationContext();
      const movement = context?.pilot.controls?.movement;
      return context && !context.pilot.dead && !context.pilot.docked && movement
        ? movement.speed * movement.rules.boostSpeedFactor * movement.boostSpeedFactor
        : null;
    },
    navigation,
  );
  let disposed = false;
  const resize = new ResizeObserver(() => {
    if (!disposed)
      app.renderer.resize(
        Math.max(1, element.clientWidth),
        Math.max(1, element.clientHeight),
      );
  });
  resize.observe(element);
  app.ticker.add((ticker) =>
    scene.render(
      performance.now(),
      Math.min(ticker.deltaMS / 1000, 0.1),
      input,
    ),
  );
  return {
    setBackdropTilt: (point) => {
      if (!disposed) scene.setBackdropTilt(point);
    },
    setFriends: (ids) => {
      if (!disposed) {
        scene.setFriends(ids);
        if (input.selectedTarget && !scene.isAttackable(input.selectedTarget))
          input.selectTarget("");
      }
    },
    setSuspended: (suspended) => {
      if (!disposed) input.setSuspended(suspended);
    },
    setSnapshot: (snapshot, receivedAt) => {
      if (!disposed) {
        scene.setSnapshot(snapshot, receivedAt);
        if (input.selectedTarget && !scene.isAttackable(input.selectedTarget))
          input.selectTarget("");
      }
    },
    setEventObjective: (objective) => {
      if (!disposed) scene.setEventObjective(objective);
    },
    setFlightIdentity: (runId, intro) => {
      if (!disposed && scene.setFlightIdentity(runId, intro))
        input.cancelNavigation();
    },
    setTutorialGuidanceVisible: (visible) => {
      if (!disposed) scene.setTutorialGuidanceVisible(visible);
    },
    setMission: (mission) => {
      if (!disposed) scene.setMission(mission);
    },
    setPartyMarker: (marker) => {
      if (!disposed) scene.setPartyMarker(marker);
    },
    setLocalPlayerId: (id) => {
      if (!disposed) scene.setLocalPlayerId(id);
    },
    setControls: (controls) => {
      if (!disposed) input.setControls(controls);
    },
    selectTarget: (id) => {
      if (!disposed) input.selectTarget(id);
    },
    navigate: (point) => {
      if (!disposed) input.navigate(point);
    },
    cancelNavigation: () => {
      if (!disposed) input.cancelNavigation();
    },
    dispose: () => {
      if (disposed) return;
      disposed = true;
      resize.disconnect();
      input.dispose();
      app.ticker.stop();
      scene.destroy();
      app.destroy({ removeView: true }, { children: true });
    },
  };
}
